// PNG export for the value charts (U-32): the chart's SVG is cloned with every
// colour, font and stroke resolved from the page's CSS (variables don't survive
// outside the document), drawn on a canvas at twice its size with the title
// above, and saved. No library.

const STYLE_PROPS = ["fill", "fill-opacity", "stroke", "stroke-width", "stroke-dasharray", "stroke-opacity", "opacity", "font-family", "font-size", "font-weight", "letter-spacing"] as const;

/** A copy of the SVG whose elements carry their computed styles inline. */
function inlineStyles(svg: SVGSVGElement): SVGSVGElement {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  const source = [svg, ...svg.querySelectorAll("*")];
  const target = [clone, ...clone.querySelectorAll("*")];
  source.forEach((node, i) => {
    const style = getComputedStyle(node as Element);
    const out = target[i] as SVGElement;
    for (const prop of STYLE_PROPS) {
      const value = style.getPropertyValue(prop);
      if (value) out.style.setProperty(prop, value);
    }
  });
  return clone;
}

/** Saves the chart as `fileName` (.png), with `title` and `subtitle` drawn above it. */
export async function downloadChartPng(svg: SVGSVGElement, fileName: string, title: string, subtitle: string): Promise<void> {
  const width = svg.width.baseVal.value || svg.getBoundingClientRect().width;
  const height = svg.height.baseVal.value || svg.getBoundingClientRect().height;
  const header = 52;
  const scale = 2;
  const clone = inlineStyles(svg);
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("width", String(width));
  clone.setAttribute("height", String(height));
  const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(clone)], { type: "image/svg+xml;charset=utf-8" }));
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("The chart could not be drawn"));
      image.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = width * scale;
    canvas.height = (height + header) * scale;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("No canvas");
    ctx.scale(scale, scale);
    const page = getComputedStyle(document.body);
    ctx.fillStyle = page.backgroundColor && page.backgroundColor !== "rgba(0, 0, 0, 0)" ? page.backgroundColor : "#0b0b0d";
    ctx.fillRect(0, 0, width, height + header);
    ctx.fillStyle = page.color || "#e8e6ef";
    ctx.font = `600 15px ${page.fontFamily}`;
    ctx.fillText(title, 12, 22);
    ctx.globalAlpha = 0.7;
    ctx.font = `12px ${page.fontFamily}`;
    ctx.fillText(subtitle, 12, 40);
    ctx.globalAlpha = 1;
    ctx.drawImage(image, 0, header, width, height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
    if (!blob) throw new Error("No image");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);
  } finally {
    URL.revokeObjectURL(url);
  }
}
